import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Newspaper, Heart, Trophy, Lock, BookOpen, Lightbulb, UserCog } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WizardSteps from "@/components/WizardSteps";
import ComingSoonModal from "@/components/ComingSoonModal";
import { useBooking } from "@/context/BookingContext";

const AD_TYPES = [
  { key: "public_notice",  label: "Public Notice",  desc: "Legal notices, property transfers, lost documents — for advocates, banks, businesses & individuals. 4 × 10 cm.", icon: Scale,   active: true },
  { key: "change_of_name", label: "Change of Name", desc: "Official name change notice — 3 × 5 cm classified. Available in Times of India, Maharashtra Times & Lokmat. Affidavit required.", icon: UserCog, active: true },
  { key: "matrimonial",    label: "Matrimonial",    desc: "Bride / Groom wanted classifieds across Marathi & English dailies.", icon: Heart, active: false },
  { key: "obituary",       label: "Obituary",       desc: "Remembrance and condolence notices, with photo option.", icon: BookOpen, active: false },
  { key: "display",        label: "Display Ad",     desc: "Quarter / Half / Full page coloured display advertisements.", icon: Newspaper, active: false },
  { key: "tender",         label: "Tender Notice",  desc: "Government & private tender publications across all editions.", icon: Trophy, active: false },
  { key: "recruitment",    label: "Recruitment",    desc: "Situations vacant ads with company branding.", icon: Lightbulb, active: false },
];

export default function AdTypeStep() {
  const nav = useNavigate();
  const { update, updateAdvocate } = useBooking();
  const [modalLabel, setModalLabel] = useState(null);

  const onSelect = (t) => {
    if (!t.active) { setModalLabel(t.label); return; }
    // Reset ad_size / ad_mode defaults based on chosen category
    if (t.key === "change_of_name") {
      update({
        ad_type: t.key,
        ad_size: "3x5",
        ad_mode: "text",
        language: "english",
      });
    } else {
      update({ ad_type: t.key, ad_size: "4x10" });
    }
    // (do not clear advocate object — user may fill once and reuse)
    void updateAdvocate; // suppress unused-var lint if not called
    nav("/book/compose");
  };

  return (
    <div className="min-h-screen flex flex-col bg-parchment">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-6 lg:px-10 py-12 w-full">
        <WizardSteps current="type" />
        <div className="text-[10px] tracking-overline uppercase text-gold font-bold">Step 01</div>
        <h1 className="font-display text-4xl lg:text-5xl text-navy-deep mt-2 mb-2">Choose your ad category.</h1>
        <p className="text-slate-600 mb-10 max-w-2xl">
          Two categories are available online right now — <strong>Public Notice</strong> and <strong>Change of Name</strong>.
          Other categories (Matrimonial, Obituary, Display, Tender, Recruitment) — call us directly.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {AD_TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => onSelect(t)}
                className={`text-left p-7 border rounded-sm transition group ${
                  t.active
                    ? "bg-white border-slate-300 hover:border-gold hover:shadow-card hover:-translate-y-0.5"
                    : "bg-slate-50 border-slate-200 hover:border-slate-300 opacity-80"
                }`}
                data-testid={`adtype-${t.key}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-sm ${
                    t.active ? "bg-navy text-parchment group-hover:bg-gold group-hover:text-navy-deep" : "bg-slate-200 text-slate-400"
                  } transition`}>
                    <Icon size={22}/>
                  </div>
                  {!t.active && <Lock size={16} className="text-slate-400" />}
                </div>
                <h3 className="font-display text-2xl text-navy-deep">{t.label}</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t.desc}</p>
                <div className={`mt-4 text-[10px] tracking-overline uppercase font-bold ${t.active ? "text-gold" : "text-slate-400"}`}>
                  {t.active ? "Available · Book online →" : "Coming Soon · Call us"}
                </div>
              </button>
            );
          })}
        </div>
      </main>
      <Footer />
      <ComingSoonModal open={!!modalLabel} onClose={() => setModalLabel(null)} adTypeLabel={modalLabel} />
    </div>
  );
}
